import {AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, UrlSegment} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {SplashScreenService} from '../parts/splash-screen/splash-screen.service';
import UserService from '../../api/user/user.service';
import {BehaviorSubject, combineLatestWith, forkJoin, Observable, of, switchMap} from 'rxjs';
import UserModel from '../../model/user.model';
import Helper from '../util/helper';
import AddOverlayService from '../parts/add-overlay/add-overlay.service';
import DeviceService from '../util/device.service';
import ArtistService from '../../api/artist/artist.service';
import ArtistModel from '../../model/artist.model';
import ArtistContext from '../util/artist.context';
import {combineLatest, debounceTime, first, map} from 'rxjs/operators';
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatSidenavContent} from "@angular/material/sidenav";
import {CrudComponent} from "../crud.component";
import ContextBarService from "../parts/context-bar/context-bar.service";

declare var $: any;


@Component({
  selector: 'app-base',
  templateUrl: './base.component.html',
  styleUrls: ['./base.component.scss']
})
export class BaseComponent implements OnInit, AfterViewInit {
  data = null;
  public opened: boolean = true;
  public rightSidebarOpened = true;


  private artistsSubject = new BehaviorSubject<ArtistModel[]>([]);
  public artists$ = this.artistsSubject.asObservable();

  public selectedArtist$: Observable<ArtistModel>;

  public smallNav = false;
  public sidenavMode = 'side';
  @ViewChild('splashScreen') splashScreen: ElementRef;
  @ViewChild('breakpointSm') breakpointSm: ElementRef;
  @ViewChild('breakpointMd') breakpointMd: ElementRef;
  @ViewChild('breakpointLg') breakpointLg: ElementRef;
  @ViewChild('breakpointXl') breakpointXl: ElementRef;
  @ViewChild("sidenavContent") contentContainer: MatSidenavContent;
  @ViewChild("artistCarousel", { read: ElementRef }) artistCarousel: ElementRef;


  public user$: Observable<UserModel>;

  private showOnlyManagedSubject = new BehaviorSubject<boolean>(false);
  public showOnlyManaged$ = this.showOnlyManagedSubject.asObservable();

  public filteredArtists$: Observable<ArtistModel[]>;

  private countUnreadSubject = new BehaviorSubject<number>(0);
  public countUnread$ = this.countUnreadSubject.asObservable();

  private updatingSubject = new BehaviorSubject<boolean>(false);
  public updating$ = this.updatingSubject.asObservable();

  public activeNav = null;

  public constructor(private titleService: Title,
                     public device: DeviceService,
                     private router: Router,
                     private snackbar: MatSnackBar,
                     public artistContext: ArtistContext,
                     private contextBarService: ContextBarService,
                     private deviceService: DeviceService,
                     private artistService: ArtistService,
                     private addOverlayService: AddOverlayService,
                     private splashScreenService: SplashScreenService,
                     private userService: UserService,
                     private route: ActivatedRoute) {
    this.titleService.setTitle('Lion CRM');
    this.user$ = this.userService.user$;

    // filtered artists if managedByMe is true
    this.filteredArtists$ = this.artists$
        .pipe(combineLatestWith(this.showOnlyManaged$))
        .pipe(switchMap(data => {
          if(data[1]) {
            return of(data[0].filter(a => a.managedByMe == true));
          } else {
            return of(data[0]);
          }
        }));
    this.selectedArtist$ = this.artistContext.selectedArtist$;

    this.selectedArtist$.subscribe(artist => {
      if(artist) {
        this.titleService.setTitle(artist.name + ' | Lion CRM');
      } else {
        this.titleService.setTitle('Lion CRM');
      }
      this.contextBarService.setArtist(artist);
    });

    // this subscription updates the swiper when the slides change
    this.filteredArtists$.pipe(debounceTime(250)).subscribe(_ => {
      if(this.artistCarousel?.nativeElement?.swiper) {
        this.artistCarousel.nativeElement.swiper.update();
      }
    });

    /*this.messageService.countUnread().subscribe(count => {
      this.countUnreadSubject.next(count);
    });*/

    this.updatingSubject.next(true);

    this.artistService.list(null, 1, 99, 'name', 'asc')
      .pipe(first())
      .subscribe(results => {
        let selectedArtistId = window.sessionStorage.getItem('artistId');
        this.artistsSubject.next(results.results);

        if(selectedArtistId) {
          let selectedArtist = this.artistsSubject.getValue().find(a => ('' + a.id) == selectedArtistId);
          if(selectedArtist) {
            this.artistContext.updateSelectedArtist(selectedArtist);
          }
        }

        this.artistContext.selectedArtist$.subscribe(artist => {
          if(artist) {
            window.sessionStorage.setItem('artistId', '' + artist.id);
          } else {
            window.sessionStorage.removeItem('artistId');
          }
        });

        this.updatingSubject.next(false);
    });
  }

  getInitials(name: string) {
    return Helper.getInitials(name);
  }

  getInitialsBg(name: string) {
    return Helper.randomColorByName(name);
  }


  ngOnInit() {
    //$.getScript('assets/js/script.js');
    this.route.data.subscribe((data) => {});

    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      if(this.deviceService.lte('sm')) {
        this.opened = false;
      }
      if(evt.urlAfterRedirects.startsWith('/messages')) {
        this.rightSidebarOpened = false;
      } else {
        this.rightSidebarOpened = !this.deviceService.eq('sm');
      }
      window.scrollTo(0, 0);
    });

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.code == 'Backquote' || e.keyCode == 192 && e.altKey || e.code == 'MetaLeft') {
        // doing
        this.addOverlayService.show();
      } else if(e.code == 'Escape') {
        this.addOverlayService.hide();
      } else {
        console.log(e);
      }
    });
  }

  openOverlay() {
    this.addOverlayService.show();
  }

  removeSplashScreen() {
  }

  ngAfterViewInit() {
    if(this.breakpointXl.nativeElement.offsetParent != null) {
      this.deviceService.setDevice('xl');
    } else if(this.breakpointLg.nativeElement.offsetParent != null) {
      this.deviceService.setDevice('lg');
    } else if(this.breakpointMd.nativeElement.offsetParent != null) {
      this.deviceService.setDevice('md');
    } else {
      this.deviceService.setDevice('sm');
    }

    if(this.deviceService.eq('sm')) {
      // sm breakpoint
      this.opened = false;
      this.sidenavMode = 'push';
    }

    this.device.device$.subscribe(size => {
      this.rightSidebarOpened = size != 'sm' && !this.router.url.startsWith('/messages');
    });
    CrudComponent.ScrollContainer = this.contentContainer;
  }

  onResize(event) {
    if(this.breakpointXl.nativeElement.offsetParent != null) {
      this.deviceService.setDevice('xl');
    } else if(this.breakpointLg.nativeElement.offsetParent != null) {
      this.deviceService.setDevice('lg');
    } else if(this.breakpointMd.nativeElement.offsetParent != null) {
      this.deviceService.setDevice('md');
    } else {
      this.deviceService.setDevice('sm');
    }
  }

  toggleNav() {
    if(this.deviceService.eq('sm')) {
      this.opened = !this.opened;
    } else {
      this.smallNav = !this.smallNav;
      this.opened = true;
    }


  }


  closeNav() {

  }

  openNav() {

  }

  isRouteActive(part: string, full?: boolean) {
    if(full) {
      return this.router.url == part;
    }
    return this.router.url.indexOf(part) >= 0;
  }

  public selectArtist(artist?: ArtistModel) {
    this.artistContext.updateSelectedArtist(artist);
  }

  public artistTracker(index: number, artist: ArtistModel) {
    return artist.id;
  }

  public logout() {
    this.userService.logout();
    this.router.navigate(['/auth/login']);
  }

  public toggleNavigationSize() {

  }

  public toggleOnlyManaged() {
    this.showOnlyManagedSubject.next(!this.showOnlyManagedSubject.getValue());
  }

  public setActiveNav(nav: string) {
    if(this.activeNav == nav) {
      this.activeNav = null;
    } else {
      this.activeNav = nav;
    }
  }

  public copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    this.snackbar.open('Text in Zwischenablage kopiert');
  }
}
